import { createFileRoute } from "@tanstack/react-router";
import { Sparkles, Star, ThumbsUp } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/reviews-hero.webp";
import destinationsImg from "@/assets/reviews-destinations.webp";
import insightsImg from "@/assets/reviews-insights.webp";

const IMG: Record<string, string> = { hero: heroImg, destinations: destinationsImg, insights: insightsImg };

export const Route = createFileRoute("/vi/platform/reviews")({
  head: () => ({
    meta: [
      { title: "Đánh giá & Phản hồi — banana.cards" },
      { name: "description", content: "Xin đánh giá Google, Trustpilot và hơn nữa — chỉ với một cú chạm. Lọc, theo dõi và phản hồi từ một nơi." },
      { property: "og:title", content: "Đánh giá & Phản hồi — banana.cards" },
      { property: "og:description", content: "Xin đánh giá Google, Trustpilot và hơn nữa — chỉ với một cú chạm." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/reviews", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Đánh giá & Phản hồi"
      title="Đánh giá 5 sao,"
      italic="chỉ một cú chạm."
      description="Sau dịch vụ, gửi link đánh giá thông minh. Khách hài lòng đi đến Google/Trustpilot. Khách không hài lòng đi đến form riêng — bạn xử lý trước khi public."
      heroImage={IMG['hero']}
      metrics={[{ v: "4x", l: "nhiều đánh giá hơn" }, { v: "4.9★", l: "rating trung bình" }, { v: "smart", l: "định tuyến" }]}
      bullets={[
        { icon: Star, title: "Định tuyến thông minh", desc: "Khách happy → public review. Khách unhappy → form riêng. Bảo vệ rating của bạn." },
        { icon: ThumbsUp, title: "Nhiều nền tảng", desc: "Google, Trustpilot, Yelp, Facebook, Tripadvisor — một link cho tất cả." },
        { icon: Sparkles, title: "AI tóm tắt", desc: "Tóm tắt cảm xúc tự động, chủ đề và điểm hành động — từ tất cả đánh giá." }
      ]}
      deepDives={[
        { eyebrow: "Điểm đến", title: "Tất cả nền tảng,", italic: "một link.", description: "Khách chọn nơi muốn đánh giá — hoặc bạn route tự động. Hoạt động trên Google, Trustpilot, Yelp, Facebook và hơn.", image: IMG["destinations"], reverse: false, points: ["Google, Trustpilot, Yelp, Facebook, Tripadvisor", "Định tuyến theo cảm xúc", "Bảng xếp hạng có thương hiệu", "QR và NFC trên thẻ"] },
        { eyebrow: "Insights", title: "Đánh giá có thông tin,", italic: "không phải chỉ ngôi sao.", description: "AI trích xuất chủ đề ('giao hàng chậm', 'nhân viên tuyệt vời') từ mọi đánh giá — thấy điều gì cần sửa.", image: IMG["insights"], reverse: true, points: ["Phân tích cảm xúc", "Trích xuất chủ đề", "Cảnh báo review tiêu cực", "Bản nháp phản hồi bằng AI"] }
      ]}
      howItWorks={[
        { title: "Bật link đánh giá", desc: "Trên thẻ và sau dịch vụ." },
        { title: "Khách tap", desc: "Trả lời nhanh: vui hay không?" },
        { title: "Route thông minh", desc: "Vui → public. Không → riêng." },
        { title: "Theo dõi & phản hồi", desc: "Tất cả trong dashboard." }
      ]}
      capabilities={["Định tuyến theo cảm xúc", "Đa nền tảng", "QR và NFC", "Nhắc tự động", "Bản nháp phản hồi bằng AI", "Trích xuất chủ đề", "Cảnh báo review tiêu cực", "Widget nhúng cho site"]}
      faq={[
        { q: "Định tuyến thông minh hoạt động thế nào?", a: "Sau dịch vụ, khách trả lời 'bạn có hài lòng không?'. Nếu có, họ đi đến trang Google/Trustpilot. Nếu không, đến form riêng để bạn xử lý trực tiếp." },
        { q: "Hỗ trợ nền tảng nào?", a: "Google Business, Trustpilot, Yelp, Facebook, Tripadvisor, Booking.com và nhiều hơn." },
        { q: "Có nhắc khách review không?", a: "Có — gửi nhắc tự động qua email/SMS sau dịch vụ, có thể cấu hình thời gian và số lần." },
        { q: "AI làm gì với đánh giá?", a: "Trích xuất cảm xúc, chủ đề chung, và nháp phản hồi cá nhân cho từng review — bạn duyệt trước khi public." }
      ]}
      outro={{ title: "Rating tốt hơn.", italic: "Insight tốt hơn.", description: "Đánh giá đẩy doanh nghiệp tiến lên — và bảo vệ thương hiệu của bạn." }}
    />
  );
}